import React from 'react';
import { type Config, usePopperTooltip } from 'react-popper-tooltip';
import { Portal } from 'react-portal';
import styled from 'styled-components';
import { useOnClickInsideOutside } from '../../hooks/useClickInsideOutside';
import { useLatest } from '../../hooks/useLatest';
import { useToggleState } from '../../hooks/useToggleState';
import { FONT_SIZE } from '../../styles';
import {
  type PopoverTooltipContainerProps,
  type PopoverTooltipProps,
  type PopperTooltipType,
} from './PopoverTooltip.types';

/**
 * This is a thin wrapper around `react-popper-tooltip`.
 *
 * @example
 *
 *     const popover = usePopoverTooltip({ visible });
 *
 *     return (<div>
 *       <div ref={popover.setTriggerRef}>Tooltip will be centered on this div.</div>
 *       <PopoverTooltip {...popover}>Tooltip contents</PopoverTooltip>
 *     </div>);
 *
 */
const PopoverTooltipComponent: React.FC<PopoverTooltipProps> = ({
  setTooltipRef,
  getTooltipProps,
  getArrowProps,
  visible,
  children,
  mode,
  portalTargetNode,
  tooltipContainerClassName,
}) => {
  return visible ? (
    <Portal node={portalTargetNode}>
      <TooltipContainer
        className={tooltipContainerClassName}
        ref={setTooltipRef}
        {...getTooltipProps()}
        mode={mode}
      >
        {children || ''}
        <div {...getArrowProps({ className: 'tooltip-arrow' })} />
      </TooltipContainer>
    </Portal>
  ) : null;
};

const PopoverTooltip = React.memo(PopoverTooltipComponent);
export { PopoverTooltip };

export const usePopoverTooltip = usePopperTooltip;

/**
 * Use with a PopoverTooltip to create an interactive menu that users can
 * click inside, and that automatically closes when the user clicks outside of it.
 *
 * @example
 *
 *     const [popover, toggleMenu, menuOpen] =
 *       usePopoverMenu({ placement: 'top-end' });
 *
 *     return (<div>
 *       <button ref={popover.setTriggerRef} onClick={toggleMenu}>Menu</button>
 *       <PopoverTooltip {...popover}>Menu contents</PopoverTooltip>
 *     </div>);
 */
export function usePopoverMenu(params: {
  placement: Config['placement'];
}): [PopperTooltipType, () => void, boolean] {
  const { placement } = params;
  const [showMenu, toggleMenu, setShowMenu] = useToggleState(false);

  const popover = usePopoverTooltip({
    visible: showMenu,
    placement,
    interactive: true,
  });

  // Close the  menu if the user clicks outside the popup.
  const filterMenuRef = useLatest(popover.tooltipRef);
  const triggerRef = useLatest(popover.triggerRef);

  useOnClickInsideOutside(
    filterMenuRef,
    (isInside) => {
      if (!isInside) {
        setShowMenu(false);
      }
    },
    { disabled: !showMenu },
    triggerRef,
  );

  return [popover, toggleMenu, showMenu];
}

const TooltipContainer = styled.div<PopoverTooltipContainerProps>`
  --tooltipBackground: ${(props) => (props.mode === 'light' ? '#fff' : '#000')};
  --tooltipBorder: ${(props) => (props.theme as { primaryColor?: string }).primaryColor ?? '#000'};
  --tooltipTextColor: ${(props) => (props.mode === 'light' ? '#000' : '#fff')};

  background-color: var(--tooltipBackground);
  border-radius: 3px;
  border: 1px solid var(--tooltipBorder);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  color: var(--tooltipTextColor);
  display: flex;
  flex-direction: column;
  padding: 0.4rem;
  transition: opacity 0.3s;
  // maximum z-index possible
  z-index: 2147483647;
  font-size: ${FONT_SIZE['14']};

  &[data-popper-interactive='false'] {
    pointer-events: none;
  }

  .tooltip-arrow {
    height: 1rem;
    position: absolute;
    width: 1rem;
    pointer-events: none;
    color: transparent;
  }

  .tooltip-arrow::before {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    width: 0;
  }

  .tooltip-arrow::after {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    position: absolute;
    width: 0;
  }

  &[data-popper-placement*='bottom'] .tooltip-arrow {
    left: 0;
    margin-top: -0.3rem;
    top: 0;
  }

  &[data-popper-placement*='bottom'] .tooltip-arrow::before {
    border-color: transparent transparent var(--tooltipBorder) transparent;
    border-width: 0 0.5rem 0.4rem 0.5rem;
    position: absolute;
    top: -1px;
  }

  &[data-popper-placement*='bottom'] .tooltip-arrow::after {
    border-color: transparent transparent var(--tooltipBackground) transparent;
    border-width: 0 0.5rem 0.4rem 0.5rem;
  }

  &[data-popper-placement*='top'] .tooltip-arrow {
    bottom: 0;
    left: 0;
    margin-bottom: -1rem;
  }

  &[data-popper-placement*='top'] .tooltip-arrow::before {
    border-color: var(--tooltipBorder) transparent transparent transparent;
    border-width: 0.4rem 0.5rem 0 0.5rem;
    position: absolute;
    top: 1px;
  }

  &[data-popper-placement*='top'] .tooltip-arrow::after {
    border-color: var(--tooltipBackground) transparent transparent transparent;
    border-width: 0.4rem 0.5rem 0 0.5rem;
  }

  &[data-popper-placement*='right'] .tooltip-arrow {
    left: 0;
    margin-left: -0.7rem;
  }

  &[data-popper-placement*='right'] .tooltip-arrow::before {
    border-color: transparent var(--tooltipBorder) transparent transparent;
    border-width: 0.5rem 0.4rem 0.5rem 0;
  }

  &[data-popper-placement*='right'] .tooltip-arrow::after {
    border-color: transparent var(--tooltipBackground) transparent transparent;
    border-width: 0.5rem 0.4rem 0.5rem 0;
    left: 6px;
    top: 0;
  }

  &[data-popper-placement*='left'] .tooltip-arrow {
    margin-right: -0.7rem;
    right: 0;
  }

  &[data-popper-placement*='left'] .tooltip-arrow::before {
    border-color: transparent transparent transparent var(--tooltipBorder);
    border-width: 0.5rem 0 0.5rem 0.4em;
  }

  &[data-popper-placement*='left'] .tooltip-arrow::after {
    border-color: transparent transparent transparent var(--tooltipBackground);
    border-width: 0.5rem 0 0.5rem 0.4em;
    left: 3px;
    top: 0;
  }
`;
